import { useState, useEffect } from 'react';
import Routes from './routes';
import { BrowserRouter } from 'react-router';
import { HelmetProvider } from 'react-helmet-async';
import ScrollToTop from '@components/ScrollToTop';
import Preloader from '@components/preloader';
import OfflineIndicator from '@components/PWA/OfflineIndicator';
import PWAInstall from '@components/PWA/PWAInstall';
import PWAUpdate from '@components/PWA/PWAUpdate';
import PWADebug from '@components/PWA/PWADebug';
import usePWA from './hooks/usePWA';
import { ThemeProvider } from './hooks/useTheme';
import AppShell from '@components/container/AppShell';
import '@components/styles/app.css';

export default function App() {
    const [isAppLoading, setIsAppLoading] = useState(true);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const { isUpdateAvailable, updateApp, registerSW } = usePWA();
    const [isUpdateBannerVisible, setIsUpdateBannerVisible] = useState(false);

    useEffect(() => {
        // Check if this is the first load
        const hasLoaded = sessionStorage.getItem('app-loaded');
        if (hasLoaded) {
            setIsFirstLoad(false);
            setIsAppLoading(false);
        } else {
            // First load - show preloader
            sessionStorage.setItem('app-loaded', 'true');
        }
    }, []);

    useEffect(() => {
        // Register service worker immediately, not just in production
        registerSW();
    }, [registerSW]);

    useEffect(() => {
        if (isUpdateAvailable) {
            setIsUpdateBannerVisible(true);
        }
    }, [isUpdateAvailable]);

    const handleLoadingComplete = () => {
        setIsAppLoading(false);
    };

    if (isAppLoading && isFirstLoad) {
        return <Preloader onLoadingComplete={handleLoadingComplete} minLoadingTime={1500} />;
    }

    return (
        <ThemeProvider>
            <HelmetProvider>
                <BrowserRouter>
                    <ScrollToTop />
                    <AppShell>
                        <Routes />
                    </AppShell>
                    <OfflineIndicator />
                    <PWAInstall />
                    <PWAUpdate
                        isUpdateAvailable={isUpdateBannerVisible}
                        onUpdate={updateApp}
                        onDismiss={() => setIsUpdateBannerVisible(false)}
                    />
                    {import.meta.env.DEV && <PWADebug />}
                </BrowserRouter>
            </HelmetProvider>
        </ThemeProvider>
    );
}
